import { useRef } from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { ClassFeature } from '../types';
import { CLASS_PRINT_MODE_STYLES } from '../constants/print';
import { useAutoScaleFont } from '../hooks/useAutoScaleFont';
import './ClassCardExpanded.css';

interface ClassCardExpandedProps {
  classFeature: ClassFeature;
  isPrintMode?: boolean;
  borderColor?: string;
}

export function ClassCardExpanded({ classFeature, isPrintMode, borderColor }: ClassCardExpandedProps) {
  const cardRef = useRef<HTMLDivElement>(null);
  const descriptionRef = useRef<HTMLDivElement>(null);
  
  const fontSize = useAutoScaleFont({
    cardRef,
    descriptionRef,
    entityId: classFeature.id,
    entityType: 'feature',
    deps: [classFeature.description, classFeature.name, isPrintMode],
  });

  // Build dynamic styles for border color
  const dynamicStyles: React.CSSProperties = {};
  if (borderColor) {
    dynamicStyles.border = `4px solid ${borderColor}`;
  }

  // Compute print mode styles with border override when in print mode
  const printStyles: React.CSSProperties | undefined = isPrintMode
    ? { ...CLASS_PRINT_MODE_STYLES, ...(borderColor ? { border: `4px solid ${borderColor}` } : {}) }
    : undefined;

  return (
    <div 
      ref={cardRef}
      className={`class-card-expanded ${isPrintMode ? 'print-mode' : ''}`}
      style={printStyles ?? (borderColor ? dynamicStyles : undefined)}
    >
      <div className="card-header">
        <h2>{classFeature.name}</h2>
        <span 
          className="level-badge"
          style={isPrintMode 
            ? { 
                background: borderColor || '#880C0C',
                // Simplified shadow for html2canvas - no inset shadows which render as sunken
                boxShadow: '0 2px 3px rgba(0, 0, 0, 0.3)',
                border: '1px solid rgba(255, 255, 255, 0.15)',
              }
            : borderColor ? { background: borderColor } : undefined
          }
        >
          {classFeature.level}
        </span>
      </div>

      {(classFeature.subclass || classFeature.featureType === 'invocation') && (
        <div className="card-meta">
          <span className="class-card-subclass-name">
            {classFeature.featureType === 'invocation' ? 'Eldritch Invocation' : classFeature.subclass}
          </span>
        </div>
      )}

      <div 
        ref={descriptionRef}
        className="card-description"
        style={{ '--scale-factor': fontSize / 100 } as React.CSSProperties}
      >
        <Markdown remarkPlugins={[remarkGfm]}>{classFeature.description}</Markdown>
      </div>

      <div className="card-footer">
        <span className="class-card-class-name">{classFeature.className}</span>
      </div>
    </div>
  );
}
